import React, { useState } from "react";

import { easasAlert } from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import { MdmDevice, MdmObject, useCreateDeviceMutation, useUpdateDeviceMutation } from "gql/generated/types";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { Fieldset } from "primereact/fieldset";
import { Tooltip } from "react-tooltip";
import DeviceForm from "./DeviceForm";

type ObjectDevicesProps = {
  object: MdmObject
}

const ObjectDevices: React.FC<ObjectDevicesProps> = ({object}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mutateFunction] = useCreateDeviceMutation({
    onCompleted: () => {
      easasAlert({
        severity: "success",
        summary: "Success",
        detail: "Device created successfully",
        life: 3000,
      });
      setDialogOpen(false);
    },
    onError: err => {
      easasAlert({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
    },
    refetchQueries: ["GetObject", "GetAllDevices"],
  });

  const handleSubmit = (device: MdmDevice) => {
    const { mdmObject, project, ...restDevice } = device
    mutateFunction({
      variables: {
        device: restDevice,
        objectId: object.id!
      }
    })
  }
  return (
    <Fieldset legend="Object's devices" toggleable>
      {object.devices && (
        <div className="flex flex-column gap-3 my-2">
          {object.devices.map((device, i) => (
            <DeviceCard key={device!.id} device={{...device!, mdmObject: object}} index={i}/>
          ))}
        </div>
      )}
      <div className="p-field flex-1">
        <Tooltip id="add-device" />
        <Button
          data-tooltip-id="add-device"
          data-tooltip-content="Add device"
          data-tooltip-place="right"
          icon="pi pi-plus"
          onClick={() => setDialogOpen(true)}
          className="p-button-outlined p-button-success p-button-rounded"
        />
        <Dialog
          onHide={() => setDialogOpen(false)}
          header="Add device"
          visible={dialogOpen}
          style={{ width: "min(100vw, 600px)" }}
        >
          <DeviceForm
            onSubmit={handleSubmit}
            device={{ identifier: "", mdmObject: object, tags: [] }}
            newForm
          />
        </Dialog>
      </div>
    </Fieldset>
  );
}

export default ObjectDevices;

const DeviceCard = ({ device, index }: { device: MdmDevice, index: number }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [mutateFunction] = useUpdateDeviceMutation({
    onCompleted: () => {
      easasAlert({
        severity: "success",
        summary: "Success",
        detail: "Device updated successfully",
        life: 3000,
      });
      setDialogOpen(false);
    },
    onError: err => {
      easasAlert({ severity: "error", summary: "Error", detail: err.message, life: 3000 });
    },
    refetchQueries: ["GetAllDevices", "GetObject"],
  });
  const handleSave = (updDevice: MdmDevice) => {
    const { project, mdmObject, createdAt, modifiedAt, ...restDevice } = updDevice
    mutateFunction({ variables: { device: { ...restDevice }, objectId: mdmObject?.id! } });
  }

  return (
    <>
      {index === 0 && (
        <div className="flex flex-nowrap justify-content-between align-items-center gap-4">
          <div className="flex-1 flex flex-column">
            <h6 className="m-0">Identifier</h6>
          </div>
          <div className="flex-1 flex flex-column">
            <h6 className="m-0">Tags</h6>
          </div>
          <div className="flex-1 white-space-nowrap flex flex-column">
            <h6 className="m-0">Created</h6>
          </div>
          <div className="flex-1 flex flex-column">
            <h6 className="m-0">Updated</h6>
          </div>
          <div className="flex-1 flex flex-column">
            <h6 className="m-0">Actions</h6>
          </div>
        </div>
      )}
      <div className="flex flex-nowrap justify-content-between align-items-center gap-4">
        <div className="flex-1">{device.identifier}</div>
        <div className="flex-1">{device.tags.join(", ")}</div>
        <div className="flex-1 white-space-nowrap">
          {new Date(device.createdAt).toLocaleDateString()}{" "}
          {new Date(device.createdAt).toLocaleTimeString()}
        </div>
        <div className="flex-1 white-space-nowrap">
          {new Date(device.modifiedAt).toLocaleDateString()}{" "}
          {new Date(device.modifiedAt).toLocaleTimeString()}
        </div>
        <div className="flex-1">
          <Button
            icon="pi pi-info-circle"
            className="p-button-rounded p-button-text"
            onClick={() => setDialogOpen(true)}
          />
          <Dialog
            onHide={() => setDialogOpen(false)}
            header="Device details"
            visible={dialogOpen}
            style={{ width: "min(100vw, 600px)" }}
          >
            <DeviceForm onSubmit={handleSave} device={device} />
          </Dialog>
        </div>
      </div>
    </>
  );
};